import {
  Box,
  Button,
  CustomSelect,
  MUIDataTable,
  MUIDataTableColumn,
  MUIDataTableColumnDef,
  SearchBar,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  Typography,
  useDataTable,
  Theme,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import Icon, { IconName } from 'web-library/stories/atom/Icon/Icon';

import { ChoosePaperSubjectModal } from '../../../components/PaperSubjectModal';
import { useRawFilters } from '../../../components/select-add-subjects/SelectAddSubjects';
import { useGetUniqueLinkSubjectDetails } from '../../../api-refactored/unique-link/useGetUniqueLinkSubjectDetails';
import {
  AdminSelectedExamSubjects,
  ExamSubjects,
  SubjectType,
  ViewSubjectDetailResponse,
} from '../../../api/candidate/exam/model';
import { usePostSearchUniqueLinkSubjects } from '../../../api-refactored/unique-link/usePostSearchUniqueLinkSubjects';
import { SubjectListingFilterDropdown } from './model';
import { getIndexValue } from '../../../utils/ArrayUtils';
import { dropDownMappingToSelect, useAPIDropdown } from '../../../hooks/dropdown';
import { candidateService } from '../../../api';
import { SelectChangeEvent } from '@mui/material';

const useStyles = makeStyles()((theme) =>
({
  datatableContainer: {
    marginBottom: theme.spacing(6),
  },
  searchInput: {
    marginBottom: theme.spacing(3),
  },
  subjectContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subjectText: {
    width: '100%',
  },
  subjectType: {
    color: theme.color.text.textTertiary,
    fontSize: '0.875rem',
    fontWeight: 800,
  },
  successAdded: {
    color: `${theme.color.state.stateSuccess} !important`,
    padding: 0,
    '& span': {
      justifyContent: 'flex-start',
    },
  },
  actionButton: {
    padding: 0,
    whiteSpace: 'nowrap',
    '& span': {
      justifyContent: 'flex-start',
      '& h5': {
        width: 'max-content',
      },
    },
  },
  select: {
    marginBottom: theme.spacing(3),
  },
}));

export const displaySubjectType = (subjectType: SubjectType) => {
  if (subjectType === SubjectType.Private) {
    return 'Private';
  }
  if (subjectType === SubjectType.School) {
    return 'School';
  }
  if (subjectType === SubjectType.Any) {
    return 'Both';
  }
  return null;
};

interface TableColumnsProps {
  adminSubjectListing: ExamSubjects[];
  adminSelectedSubjects: AdminSelectedExamSubjects[];
  setAdminSelectedSubjects: React.Dispatch<React.SetStateAction<AdminSelectedExamSubjects[]>>;
  setAdminEditedSubjectDetails: React.Dispatch<React.SetStateAction<ExamSubjects>>;
  setSelectedSubjectCodeForChoosePapers: React.Dispatch<React.SetStateAction<string>>;
  examLevels: SubjectListingFilterDropdown;
  examSeries: SubjectListingFilterDropdown;
  languages: SubjectListingFilterDropdown;
  classes: Record<string,string>;
  t: TFunction<'translation'>;
  theme: Theme;
}

const tableColumns = ({
  adminSubjectListing,
  adminSelectedSubjects,
  setAdminSelectedSubjects,
  setAdminEditedSubjectDetails,
  setSelectedSubjectCodeForChoosePapers,
  examLevels,
  examSeries,
  languages,
  classes,
  t,
  theme,
}: TableColumnsProps): MUIDataTableColumnDef[] => {
  const filterOnChange = (
    filterList: string[][],
    onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
    index: number,
    column: MUIDataTableColumn,
    event: SelectChangeEvent<unknown>
  ) => {
    // eslint-disable-next-line no-param-reassign
    filterList[index] = event.target.value as string[];
    onChange(filterList[index], index, column);
    window.dispatchEvent(new CustomEvent('resize'));
  };
  return [
    {
      name: 'subjectName',
      label: t('exam->>SelectSubjects.SubjectName'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.subjectContainer}>
            <Typography className={classes.subjectText} variant="body2">
              {adminSubjectListing[dataIndex].subjectName} (
              {adminSubjectListing[dataIndex].subjectCode})
            </Typography>
            <Typography className={classes.subjectType}>
              {displaySubjectType(adminSubjectListing[dataIndex].subjectType as SubjectType)}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'subjectLevel',
      label: t('exam->>SelectSubjects.SubjectLevel'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '10%' } }),
      },
    },
    {
      name: 'examSeries',
      label: t('exam->>SelectSubjects.ExamSeries'),
      options: {
        sort: true,
        searchable: true,
        filter: true,
        filterType: 'custom',
        setCellProps: () => ({ style: { width: '15%' } }),
        filterOptions: {
          fullWidth: true,
          display: (
            filterList: string[][],
            onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
            index: number,
            column: MUIDataTableColumn
          ) => {
            const { functionCode: filterCodeExamSeries } = examSeries;
            return (
              <CustomSelect
                className={classes.select}
                displayEmpty
                variant="filled"
                multiple
                // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
                value={filterList[index]}
                menuItems={filterCodeExamSeries}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  filterOnChange(filterList, onChange, index, column, e);
                }}
                label={t('exam->>SelectSubjects.ExamSeries')}
                labelId="inputSelectLabel-examSeries"
                id="inputSelect-examSeries"
                renderValue={(selected: unknown | string[]) =>
                  Array.isArray(selected) && selected.length > 0
                    ? // @ts-ignore
                    filterCodeExamSeries
                      .filter((examSeriesValue) => selected.includes(examSeriesValue.value))
                      .map((s) => s.children)
                      .join(', ')
                    : 'Select Exam Series'
                }
              />
            );
          },
        },
      },
    },
    {
      name: 'examLevel',
      label: t('exam->>SelectSubjects.ExamLevel'),
      options: {
        sort: true,
        searchable: true,
        filter: true,
        filterType: 'custom',
        setCellProps: () => ({ style: { width: '16%' } }),
        filterOptions: {
          fullWidth: true,
          display: (
            filterList: string[][],
            onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
            index: number,
            column: MUIDataTableColumn
          ) => {
            const { functionCode: filterCodeExamLevel } = examLevels;
            return (
              <CustomSelect
                className={classes.select}
                // eslint-disable-next-line react/destructuring-assignment
                defaultValue={filterList[index][0]}
                displayEmpty
                menuItems={filterCodeExamLevel}
                onChange={(event) => {
                  // eslint-disable-next-line no-param-reassign
                  filterList[index][0] = event.target.value as string;
                  onChange(filterList[index], index, column);
                }}
                label={t('exam->>SelectSubjects.ExamLevel')}
                labelId="inputSelectLabel-examLevel"
                id="inputSelect-examLevel"
              />
            );
          },
        },
      },
    },
    {
      name: 'language',
      label: t('exam->>SelectSubjects.LanguageMedium'),
      options: {
        sort: true,
        searchable: true,
        filter: true,
        filterType: 'custom',
        setCellProps: () => ({ style: { width: '10%' } }),
        filterOptions: {
          fullWidth: true,
          display: (
            filterList: string[][],
            onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
            index: number,
            column: MUIDataTableColumn
          ) => {
            const { functionCode: filterCodeLanguages } = languages;
            return (
              <CustomSelect
                className={classes.select}
                displayEmpty
                variant="filled"
                multiple
                // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
                value={filterList[index]}
                menuItems={filterCodeLanguages}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  filterOnChange(filterList, onChange, index, column, e);
                }}
                label="Languages"
                labelId="inputSelectLabel-languages"
                id="inputSelect-languages"
                renderValue={(selected: unknown | string[]) =>
                  Array.isArray(selected) && selected.length > 0
                    ? // @ts-ignore
                    filterCodeLanguages
                      .filter((language: any) => selected.includes(language.value))
                      .map((s: any) => s.children)
                      .join(', ')
                    : 'Select Languages'
                }
              />
            );
          },
        },
      },
    },
    {
      name: 'action',
      label: t('exam->>SelectSubjects.Action'),
      options: {
        sort: false,
        searchable: false,
        filter: false,
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const { actionRequired, subjectCode } = adminSubjectListing[dataIndex];
          const labelActionRequired: string = actionRequired
            ? t('exam->>SelectSubjects.ChoosePapers')
            : t('exam->>SelectSubjects.AddSubject');

          const isAdminAddedSubject = adminSelectedSubjects.find(
            (selectedSubject: AdminSelectedExamSubjects) =>
              selectedSubject.subjectCode === subjectCode
          );

          return (
            <Box>
              {isAdminAddedSubject ? (
                <Box className={classes.successAdded}>
                  <IconWithLabel
                    iconSize="S"
                    iconName={IconName.tick}
                    label={t('exam->>SelectSubjects.Added')}
                    typographyProps={{
                      variant: 'h5',
                    }}
                  />
                </Box>
              ) : (
                <Button
                  className={classes.actionButton}
                  variant="text"
                  color="primary"
                  type="button"
                  onClick={() => {
                    if (labelActionRequired === t('exam->>SelectSubjects.ChoosePapers')) {
                      setAdminEditedSubjectDetails(adminSubjectListing[dataIndex]);
                      setSelectedSubjectCodeForChoosePapers(
                        adminSubjectListing[dataIndex].subjectCode
                      );
                    }
                    if (labelActionRequired === t('exam->>SelectSubjects.AddSubject')) {
                      setAdminSelectedSubjects(
                        (existingAdminSelectedSubjects: AdminSelectedExamSubjects[]) => [
                          ...existingAdminSelectedSubjects,
                          adminSubjectListing[dataIndex] as AdminSelectedExamSubjects,
                        ]
                      );
                    }
                  }}
                  size="small"
                  id={`add-subject-${dataIndex}`}
                  startIcon={
                    <Icon name={IconName.add} size="S" color={theme.color.link.linkDefault01} />
                  }
                >
                  {labelActionRequired}
                </Button>
              )}
            </Box>
          );
        },
      },
    },
  ];
};

interface AdminSubjectListingProps {
  adminSelectedSubjects: AdminSelectedExamSubjects[];
  setAdminSelectedSubjects: React.Dispatch<React.SetStateAction<AdminSelectedExamSubjects[]>>;
  adminEditedSubjectDetails: ExamSubjects;
  setAdminEditedSubjectDetails: React.Dispatch<React.SetStateAction<ExamSubjects>>;
}

const AdminSubjectListing = ({
  adminSelectedSubjects,
  setAdminSelectedSubjects,
  adminEditedSubjectDetails,
  setAdminEditedSubjectDetails,
}: AdminSubjectListingProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const theme = useTheme();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'subjectName',
    order: 'asc',
    size: 10,
    page: 0,
    search: '',
    filters: {},
  });

  const [
    selectedSubjectCodeForChoosePapers,
    setSelectedSubjectCodeForChoosePapers,
  ] = useState<string>('');

  const examLevels = useAPIDropdown(
    () => candidateService.exam.candidate.usePostExamLevel({ examType: 'GCE' }),
    true,
    dropDownMappingToSelect
  );

  const { examSeries, languages } = useRawFilters('');

  const {
    mutate: mutatePostSearchUniqueLinkSubjects,
    isLoading: isLoadingSearchUniqueLinkSubjects,
  } = usePostSearchUniqueLinkSubjects({
    onSuccess: (data) =>
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes),
  });

  useEffect(() => {
    mutatePostSearchUniqueLinkSubjects({
      reqBody: {
        searchParam: state.search,
        examLevelUuid: getIndexValue(state.filters.examLevel, 0),
        examSeriesUuid: state.filters.examSeries || [],
        languageUuid: state.filters.language || [],
      },
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        sort: state.sortColumn,
        order: state.order,
      },
    });
  }, [
    mutatePostSearchUniqueLinkSubjects,
    state.filters.examLevel,
    state.filters.examSeries,
    state.filters.language,
    state.order,
    state.page,
    state.search,
    state.size,
    state.sortColumn,
  ]);

  const { data: subjectDetails } = useGetUniqueLinkSubjectDetails({
    subjectCode: selectedSubjectCodeForChoosePapers,
  });

  return (
    <Box className={classes.datatableContainer}>
      <SearchBar
        className={classes.searchInput}
        placeholder={t('exam->>SelectSubjects.SearchSubjectsAdmin')}
        name="searchInput"
        ariaLabel="searchInput"
        defaultValue={state.search}
        onSearch={(searchStr: string) => {
          tableDispatch({
            type: 'UPDATE_SEARCH',
            payload: searchStr.trim(),
          });
        }}
        clearInputButton
        preventEnterKeyPropagation
      />
      <MUIDataTable
        state={state}
        isLoading={isLoadingSearchUniqueLinkSubjects}
        dispatch={tableDispatch}
        columns={tableColumns({
          adminSubjectListing: state.data,
          adminSelectedSubjects,
          setAdminSelectedSubjects,
          setAdminEditedSubjectDetails,
          setSelectedSubjectCodeForChoosePapers,
          examLevels,
          examSeries,
          languages,
          classes,
          t,
          theme,
        })}
        data={state.data}
        options={{
          ...tOptions,
          filter: true,
          pagination: true,
          sort: true,
          selectToolbarPlacement: 'none',
        }}
      />
      {subjectDetails && (
        <ChoosePaperSubjectModal
          applicationUuid=""
          modalData={subjectDetails as ViewSubjectDetailResponse}
          onClose={() => setSelectedSubjectCodeForChoosePapers('')}
          modelOpen={
            !!selectedSubjectCodeForChoosePapers &&
            !!(subjectDetails.examSeries.length > 1 || subjectDetails.optionalPapers.length)
          }
          mutateSubjectHandler={{
            mutateSubjectStatus: 'idle',
            mutateSubjectReset: () => null,
          }}
          mutateChoosePaperSubject={(subjectData) => {
            const selectedExamSeries = subjectDetails?.examSeries.find(
              (examSeriesValue) => examSeriesValue.code === subjectData.examSeriesUuid
            );
            setAdminSelectedSubjects(
              (existingAdminSelectedSubjects: AdminSelectedExamSubjects[]) => [
                ...existingAdminSelectedSubjects,
                {
                  ...adminEditedSubjectDetails,
                  examSeries: selectedExamSeries?.description as string,
                  examSeriesUuid: subjectData?.examSeriesUuid,
                  optionalPapers: subjectData.optionalPapers,
                },
              ]
            );
          }}
        />
      )}
    </Box>
  );
};

export default AdminSubjectListing;
